/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { Database } from "../database";

/**
 * Represents a Consultant.
 * @class Consultant
 */
export class Consultant {
    consultantID: bigint;
    name: string;

    /**
     * @constructor
     * @param {bigint} consultantID - The ID of the consultant.
     * @param {string} name - The name of the consultant. 
     */
    constructor(consultantID: bigint, name: string) {
        this.consultantID = consultantID;
        this.name = name;
    }
    
    /**
     * @async
     * @function save - Saves the new consultant
     * @param {Database} database - Database which is referencing the SQL statement 
     * @param {string} accessToken - Access token to execute SQL statement in the database 
     */
    async save(database: Database, accessToken: string) {
        const sql = `INSERT INTO Consultant (consultantID, name) VALUES (${this.consultantID},'${this.name}')`;
        database.readWrite(accessToken, sql);
    }
    
    /**
     * 
     * @param {Database} database - Database which is referencing the SQL statement 
     * @param {string} accessToken - Access token to execute SQL statement in the database 
     * @returns 
     */
    static async list(database: Database, accessToken: string): Promise<Consultant[]> {
        const consultants: Consultant[] = [];
        if (database) {
          const sql = "SELECT * FROM Consultant;";
          const rows = await database.read(accessToken, sql);
          rows.forEach((row: any) => {
            const consultant = new Consultant(BigInt(row.consultantID), row.name);
            consultants.push(consultant);
          });
        }
        return consultants;
    }

    /**
     * @async
     * @function updateConsultant
     * @param {Database} database - Database which is referencing the SQL statement 
     * @param {string} accessToken - Access token to execute SQL statement in the database 
     */
    async updateConsultant(database: Database, accessToken: string){
        const sql = `UPDATE Consultant SET name='${this.name}' WHERE consultantID = ${this.consultantID}`;
        database.readWrite(accessToken, sql);
    }

    /**
     * @async
     * @function deleteConsultant - Deletes the single selected consultant
     * @param {Database} database - Database which is referencing the SQL statement 
     * @param {string} accessToken - Access token to execute SQL statement in the database 
     */
    async deleteConsultant(database: Database, accessToken: string): Promise<void>{
        const sql = `DELETE FROM Consultant WHERE consultantID = ${this.consultantID}`;
        database.readWrite(accessToken, sql);
    }

    /**
     * @static
     * @async
     * @function deleteConsultants - Delete all selected consultants
     * @param {Database} database - Database which is referencing the SQL statement 
     * @param {string} accessToken - Access token to execute SQL statement in the database 
     * @param consultantIds 
     */
    static async deleteConsultants(database: Database, accessToken: string, consultantIds: Array<bigint>): Promise<void>{
        const sql = `DELETE FROM Consultant WHERE consultantID in (${consultantIds.join(
            ","
        )})`;
        database.readWrite(accessToken, sql);
    }
}